import qs from 'query-string';

import Service from './Service';
import NavigationService from './NavigationService';
import {isDocumentsMode, isStipsMode} from '-/util/util';
import {
    AssignableLandingPages,
    DefaultLandingPage,
    LandingPageSources,
    ParamLandingPages
} from '-/util/landingPages';

const UISERVICE_INITIALIZE = 'UISERVICE_INITIALIZE';
const UISERVICE_LOADING = 'UISERVICE_LOADING';
const UISERVICE_ERROR = 'UISERVICE_ERROR';
const UISERVICE_SETDATA = 'UISERVICE_SETDATA';
const UISERVICE_STEPCOMPLETED = 'UISERVICE_STEPCOMPLETED';

const reducerName = 'ui';

const initialState = {
    initialized: false,
    loading: false,
    error: null,
    fromWorkflow: false,
    stepsCompleted: [],
    stepsCompleted_CoApp: [],
    status: null
};

const stepsCompleted = (state, step) => {
    const isCoApp = NavigationService.isCoApplicant();
    const stepsCompleted = isCoApp ? state.stepsCompleted_CoApp : state.stepsCompleted;
    let newSteps = stepsCompleted;
    if (stepsCompleted.indexOf(step) < 0) {
        newSteps = [...stepsCompleted, step];
    }
    return {
        ...state,
        [isCoApp ? 'stepsCompleted_CoApp' : 'stepsCompleted']: newSteps
    };
};

const reducer = (state = initialState, action) => {
    const {type, payload} = action;
    switch (type) {
        case UISERVICE_INITIALIZE:
            return {
                ...state,
                initialized: true,
                status: payload
            };
        case UISERVICE_LOADING:
            return {
                ...state,
                loading: payload
            };
        case UISERVICE_ERROR:
            return {
                ...state,
                error: payload
            };
        case UISERVICE_SETDATA: {
            const {fromWorkflow, stepsCompleted, stepsCompleted_CoApp, status} = payload;
            return {
                ...state,
                fromWorkflow,
                stepsCompleted,
                stepsCompleted_CoApp
            };
        }
        case UISERVICE_STEPCOMPLETED:
            return stepsCompleted(state, payload);
    }
    return state;
};

class UIService extends Service {
    constructor() {
        super();

        if (!UIService.instance) {
            UIService.instance = this;
        }
        return UIService.instance;
    }

    getReducerName() {
        return reducerName;
    }

    getReducer() {
        return reducer;
    }

    initialize(status) {
        this.store.dispatch({
            type: UISERVICE_INITIALIZE,
            payload: status
        });
    }

    setLoading(loading) {
        this.store.dispatch({
            type: UISERVICE_LOADING,
            payload: loading
        });
    }

    setError(error) {
        this.store.dispatch({
            type: UISERVICE_ERROR,
            payload: error
        });
    }

    stepCompleted(step) {
        this.store.dispatch({
            type: UISERVICE_STEPCOMPLETED,
            payload: step
        });
    }

    getKeyName() {
        return `stepsCompleted${NavigationService.isCoApplicant() ? '_CoApp' : ''}`;
    }

    getFromWorkflow() {
        return this.getReducerState().fromWorkflow;
    }

    getStatus() {
        return this.getReducerState().status;
    }

    getStepsForCurrentApplicant() {
        return this.getReducerState()[this.getKeyName()];
    }

    setData(data) {
        const {fromWorkflow = false, stepsCompleted = [], stepsCompleted_CoApp = []} = data;
        this.store.dispatch({
            type: UISERVICE_SETDATA,
            payload: {
                fromWorkflow,
                stepsCompleted,
                stepsCompleted_CoApp
            }
        });
    }

    getDataForSave() {
        const stepsCompleted = this.getStepsForCurrentApplicant();
        return {[this.getKeyName()]: stepsCompleted};
    }

    /**
     * Which landing page to render, and how it was chosen.
     *
     * `?t=` beats the offer's own `landingPage` on purpose: the offer field is what the
     * mail drop set, and the param is how anyone -- marketing, support, a test -- looks
     * at a different page for the same member without editing their record.
     *
     * `source` is reported with every funnel event, because the same arm reached two
     * ways is two populations: a `?t=` view is usually someone checking a URL, an
     * `assigned` view is a member opening their mail.
     *
     * Stips and documents mode are not variants and never carry a source of their own --
     * they are the URL saying "this is not a landing page at all".
     *
     * @returns {{pageType: String, source: String}}
     */
    getLandingPage() {
        const {t} = qs.parse(location.search);
        const {ASSIGNED, DEFAULT, PARAM} = LandingPageSources;

        if (isStipsMode()) {
            return {pageType: 'stips', source: DEFAULT};
        }

        if (t && ParamLandingPages.includes(t)) {
            return {pageType: t, source: PARAM};
        }

        const {landingPage} = this.getState().config || {};

        if (landingPage && AssignableLandingPages.includes(landingPage)) {
            return {pageType: landingPage, source: ASSIGNED};
        }

        return {pageType: DefaultLandingPage, source: DEFAULT};
    }

    getPageType() {
        return this.getLandingPage().pageType;
    }
}

export default new UIService();
